/**
 * Interpretação do resultado inicial de um Kaizen.
 * Cálculo determinístico. A decisão humana fica na tabela de validações.
 * Custo não entra: metrics_snapshots não guarda custo.
 */

export const RESULT_METRIC_KEYS = ['lead_time', 'cycle_time', 'ftr', 'capacity_fte'] as const;

export type ResultMetricKey = (typeof RESULT_METRIC_KEYS)[number];
export type MetricDirection = 'lower_is_better' | 'higher_is_better';
export type ResultAssessment = 'positive' | 'neutral' | 'negative';
export type TargetAssessment = 'met' | 'not_met' | 'unavailable';
export type ResultDecision = 'validated' | 'rejected';

export interface KaizenResultSource {
  id: string;
  organization_id: string | null;
  implementation_date?: string | Date | null;
  baseline_lead_time: number | null;
  target_lead_time: number | null;
  baseline_cycle_time: number | null;
  target_cycle_time: number | null;
  baseline_ftr: number | null;
  target_ftr: number | null;
  baseline_capacity_fte: number | null;
  target_capacity_fte: number | null;
}

export interface SnapshotResultSource {
  id: string;
  kaizen_id: string;
  measured_at: string | Date;
  created_by?: string | null;
  lead_time: number | null;
  cycle_time: number | null;
  ftr: number | null;
  capacity_fte: number | null;
}

export interface EvidenceMetric {
  key: ResultMetricKey;
  baseline: number;
  target: number | null;
  measured: number;
}

export interface EvidenceContext {
  snapshot_id: string;
  measured_at: string;
  metrics: EvidenceMetric[];
}

export interface InterpretedMetric extends EvidenceMetric {
  direction: MetricDirection;
  assessment: ResultAssessment;
  improvementPercent: number | null;
  targetAssessment: TargetAssessment;
}

export type ResultUiState =
  | 'missing_implementation_date'
  | 'missing_eligible_snapshot'
  | 'no_comparable_baseline'
  | 'awaiting'
  | 'validated'
  | 'rejected';

const LABELS: Record<ResultMetricKey, string> = {
  lead_time: 'Lead Time',
  cycle_time: 'Cycle Time',
  ftr: 'FTR',
  capacity_fte: 'Capacidade',
};

export function metricLabel(key: ResultMetricKey): string {
  return LABELS[key];
}

/**
 * FTR melhora quando sobe. Lead time e cycle time melhoram quando descem.
 * capacity_fte permanece menor-é-melhor porque o cadastro trata FTE
 * necessário: a redução planejada é baseline − target, e o modelo de
 * importação usa alvo abaixo da partida. Isso não é a capacidade liberada
 * do card, que lê kaizen_roi.annual_fte_freed.
 */
export function metricDirection(key: ResultMetricKey): MetricDirection {
  return key === 'ftr' ? 'higher_is_better' : 'lower_is_better';
}

export function finiteMetric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function assessResult(
  direction: MetricDirection,
  baseline: number | null,
  measured: number | null,
): ResultAssessment | null {
  if (baseline == null || measured == null) return null;
  if (measured === baseline) return 'neutral';
  const improved = direction === 'lower_is_better' ? measured < baseline : measured > baseline;
  return improved ? 'positive' : 'negative';
}

export function improvementPercent(
  direction: MetricDirection,
  baseline: number | null,
  measured: number | null,
): number | null {
  if (baseline == null || measured == null || baseline === 0) return null;
  const delta = direction === 'lower_is_better' ? baseline - measured : measured - baseline;
  const percent = (delta / baseline) * 100;
  return Number.isFinite(percent) ? percent : null;
}

export function assessTarget(
  direction: MetricDirection,
  target: number | null,
  measured: number | null,
): TargetAssessment {
  if (target == null || measured == null) return 'unavailable';
  const met = direction === 'lower_is_better' ? measured <= target : measured >= target;
  return met ? 'met' : 'not_met';
}

function pair(
  key: ResultMetricKey,
  baseline: number | null,
  target: number | null,
  measured: number | null,
): EvidenceMetric | null {
  if (baseline == null || measured == null) return null;
  return { key, baseline, target, measured };
}

export function evidenceMetrics(
  kaizen: KaizenResultSource,
  snapshot: SnapshotResultSource,
): EvidenceMetric[] {
  return [
    pair('lead_time', kaizen.baseline_lead_time, kaizen.target_lead_time, snapshot.lead_time),
    pair('cycle_time', kaizen.baseline_cycle_time, kaizen.target_cycle_time, snapshot.cycle_time),
    pair('ftr', kaizen.baseline_ftr, kaizen.target_ftr, snapshot.ftr),
    pair('capacity_fte', kaizen.baseline_capacity_fte, kaizen.target_capacity_fte, snapshot.capacity_fte),
  ].filter((metric): metric is EvidenceMetric => metric != null);
}

export function toIso(value: string | Date): string | null {
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? date.toISOString() : null;
}

export function isOnOrAfterImplementation(
  implementationDate: string | Date | null | undefined,
  measuredAt: string | Date | null | undefined,
): boolean {
  if (implementationDate == null || measuredAt == null) return false;
  const start = new Date(implementationDate).getTime();
  const measured = new Date(measuredAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(measured)) return false;
  return measured >= start;
}

export function buildEvidenceContext(
  kaizen: KaizenResultSource,
  snapshot: SnapshotResultSource,
): EvidenceContext | null {
  const measuredAt = toIso(snapshot.measured_at);
  const metrics = evidenceMetrics(kaizen, snapshot);
  if (!measuredAt || metrics.length === 0) return null;
  return {
    snapshot_id: snapshot.id,
    measured_at: measuredAt,
    metrics,
  };
}

export function interpretMetric(metric: EvidenceMetric): InterpretedMetric {
  const direction = metricDirection(metric.key);
  const assessment = assessResult(direction, metric.baseline, metric.measured) ?? 'neutral';
  return {
    ...metric,
    direction,
    assessment,
    improvementPercent: improvementPercent(direction, metric.baseline, metric.measured),
    targetAssessment: assessTarget(direction, metric.target, metric.measured),
  };
}

export function interpretEvidence(context: EvidenceContext): InterpretedMetric[] {
  return context.metrics.map(interpretMetric);
}

function isMetricKey(value: unknown): value is ResultMetricKey {
  return RESULT_METRIC_KEYS.includes(value as ResultMetricKey);
}

export function readEvidenceContext(value: unknown): EvidenceContext | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.snapshot_id !== 'string' || typeof record.measured_at !== 'string') return null;
  if (!Array.isArray(record.metrics)) return null;
  const metrics: EvidenceMetric[] = [];
  for (const item of record.metrics) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const metric = item as Record<string, unknown>;
    if (!isMetricKey(metric.key)) return null;
    const baseline = finiteMetric(metric.baseline);
    const measured = finiteMetric(metric.measured);
    const target = metric.target == null ? null : finiteMetric(metric.target);
    if (metric.target != null && target == null) return null;
    if (baseline == null || measured == null) continue;
    metrics.push({ key: metric.key, baseline, target, measured });
  }
  if (metrics.length === 0) return null;
  return { snapshot_id: record.snapshot_id, measured_at: record.measured_at, metrics };
}

export function isResultDecision(value: unknown): value is ResultDecision {
  return value === 'validated' || value === 'rejected';
}

export function rejectionNoteIsMissing(decision: ResultDecision, note: string | null | undefined): boolean {
  if (decision !== 'rejected') return false;
  return note == null || note.trim() === '';
}

export function samePersonMeasuredAndValidated(
  snapshotCreatedBy: string | null | undefined,
  validationCreatedBy: string | null | undefined,
): boolean {
  return Boolean(snapshotCreatedBy) && snapshotCreatedBy === validationCreatedBy;
}

export function eligibleSnapshots(
  kaizen: KaizenResultSource,
  snapshots: SnapshotResultSource[],
): SnapshotResultSource[] {
  return snapshots
    .filter((snapshot) => (
      snapshot.kaizen_id === kaizen.id
      && isOnOrAfterImplementation(kaizen.implementation_date, snapshot.measured_at)
      && evidenceMetrics(kaizen, snapshot).length > 0
    ))
    .sort((left, right) => new Date(right.measured_at).getTime() - new Date(left.measured_at).getTime());
}

export function resultUiState(input: {
  kaizen: KaizenResultSource;
  snapshots: SnapshotResultSource[];
  latestValidation: { snapshot_id: string; decision: ResultDecision } | null;
}): { state: ResultUiState; canValidate: boolean; message: string | null } {
  if (input.kaizen.implementation_date == null) {
    return {
      state: 'missing_implementation_date',
      canValidate: false,
      message: 'Informe a data de implementação antes de validar o resultado.',
    };
  }
  const afterImplementation = input.snapshots.filter((snapshot) => (
    snapshot.kaizen_id === input.kaizen.id
    && isOnOrAfterImplementation(input.kaizen.implementation_date, snapshot.measured_at)
  ));
  if (afterImplementation.length === 0) {
    return {
      state: 'missing_eligible_snapshot',
      canValidate: false,
      message: 'Registre uma medição após a implementação para validar o resultado.',
    };
  }
  const eligible = eligibleSnapshots(input.kaizen, input.snapshots);
  if (eligible.length === 0) {
    return {
      state: 'no_comparable_baseline',
      canValidate: false,
      message: 'Não há baseline comparável nesta medição.',
    };
  }
  const latest = eligible[0];
  const validation = input.latestValidation;
  if (!validation || validation.snapshot_id !== latest.id) {
    return { state: 'awaiting', canValidate: true, message: null };
  }
  return {
    state: validation.decision === 'validated' ? 'validated' : 'rejected',
    canValidate: true,
    message: null,
  };
}

export const RESULT_UI_LABEL: Record<ResultUiState, string> = {
  missing_implementation_date: 'Sem medição elegível',
  missing_eligible_snapshot: 'Sem medição elegível',
  no_comparable_baseline: 'Sem medição elegível',
  awaiting: 'Aguardando validação',
  validated: 'Resultado validado',
  rejected: 'Validação rejeitada',
};

const INSERT_KEYS = [
  'organization_id',
  'kaizen_id',
  'snapshot_id',
  'decision',
  'note',
  'evidence_context',
  'created_by',
] as const;

export interface ValidationInsert {
  organization_id: string;
  kaizen_id: string;
  snapshot_id: string;
  decision: ResultDecision;
  note: string | null;
  evidence_context: EvidenceContext;
  created_by: string;
}

export function buildValidationInsert(input: {
  kaizen: KaizenResultSource;
  snapshot: SnapshotResultSource;
  decision: ResultDecision;
  note: string | null | undefined;
  sessionUserId: string;
}): ValidationInsert {
  const context = buildEvidenceContext(input.kaizen, input.snapshot);
  if (!input.kaizen.organization_id || !context) {
    throw new Error('Não foi possível registrar a validação.');
  }
  const payload: ValidationInsert = {
    organization_id: input.kaizen.organization_id,
    kaizen_id: input.kaizen.id,
    snapshot_id: input.snapshot.id,
    decision: input.decision,
    note: input.note?.trim() ? input.note.trim() : null,
    evidence_context: context,
    created_by: input.sessionUserId,
  };
  const keys = Object.keys(payload);
  if (keys.length !== INSERT_KEYS.length || INSERT_KEYS.some((key) => !keys.includes(key))) {
    throw new Error('Não foi possível registrar a validação.');
  }
  return payload;
}

export function prependValidation<T>(history: readonly T[], attempt: T): T[] {
  return [attempt, ...history];
}
